/**
 * The AI feature builder (ADR 0025): the user describes a feature, Claude
 * proposes a plugin (`propose_plugin`), Nexus checks it — valid manifest, code
 * that runs in the sandbox and registers what the manifest promises — and asks
 * Claude to fix it once if not. The conversation is kept, so "change this…"
 * refines the same plugin.
 */
import {
  AiError,
  callOnce,
  type AiModel,
  type AiUsage,
  type ContentBlock,
  type MessageParam,
  type Transport,
} from '@nexus/ai';
import { readManifest, type PluginManifest } from './manifest';

export interface PluginProposal {
  manifest: PluginManifest;
  code: string;
  summary: string;
  notes: string;
}

export interface BuilderState {
  /** The conversation so far; ends with the last proposal (its tool call answered on the next turn). */
  messages: MessageParam[];
  pendingToolUseId: string | null;
}

export const EMPTY_BUILDER: BuilderState = { messages: [], pendingToolUseId: null };

const MAX_FIXES = 1;

/** The user's request as the next user turn (answering the previous proposal, if any). */
function userTurn(state: BuilderState, text: string): MessageParam {
  return state.pendingToolUseId
    ? {
        role: 'user',
        content: [
          {
            type: 'tool_result',
            tool_use_id: state.pendingToolUseId,
            content: 'The user saw the preview of this version.',
          },
          { type: 'text', text },
        ],
      }
    : { role: 'user', content: text };
}

/** First message when refining an installed plugin: its current version, then the request. */
export function refineRequest(
  current: { manifest: PluginManifest; code: string },
  request: string,
) {
  return [
    'Here is the current version of the plugin. Change it as asked and propose the complete new version (same manifest id).',
    '',
    'Manifest:',
    JSON.stringify(current.manifest, null, 2),
    '',
    'Code:',
    current.code,
    '',
    `Request: ${request}`,
  ].join('\n');
}

export async function buildPlugin(opts: {
  transport: Transport;
  model: AiModel;
  state: BuilderState;
  request: string;
  /** Problems with a proposal (sandbox check), or null when it is fine. */
  check: (manifest: PluginManifest, code: string) => Promise<string | null>;
  onText?: (text: string) => void;
  onUsage?: (usage: AiUsage) => void;
  signal?: AbortSignal;
}): Promise<{ proposal: PluginProposal; state: BuilderState }> {
  let messages: MessageParam[] = [...opts.state.messages, userTurn(opts.state, opts.request)];

  for (let attempt = 0; ; attempt++) {
    let text = '';
    const done = await callOnce(
      opts.transport,
      { mode: 'plugin', model: opts.model, messages },
      opts.signal,
      {
        onText: (t) => {
          text += t;
          opts.onText?.(text);
        },
        onUsage: opts.onUsage,
      },
    );
    if (done.stop_reason === 'refusal') throw new AiError('refusal');
    const content = done.content as ContentBlock[];
    const call = content.find(
      (b): b is Extract<ContentBlock, { type: 'tool_use' }> =>
        b.type === 'tool_use' && b.name === 'propose_plugin',
    );
    if (!call) throw new AiError('api', text.trim() || 'Claude stelde geen plugin voor.');

    const input = (call.input ?? {}) as Record<string, unknown>;
    const m = readManifest(input.manifest);
    const code = typeof input.code === 'string' ? input.code : '';
    const problem =
      'error' in m ? m.error : !code ? 'code is empty' : await opts.check(m.manifest, code);
    messages = [...messages, { role: 'assistant', content }];

    if (!problem && 'manifest' in m) {
      return {
        proposal: {
          manifest: m.manifest,
          code,
          summary: typeof input.summary === 'string' ? input.summary : '',
          notes: typeof input.notes === 'string' ? input.notes : '',
        },
        state: { messages, pendingToolUseId: call.id },
      };
    }
    if (attempt >= MAX_FIXES)
      throw new AiError('api', `De voorgestelde plugin werkt niet: ${problem}`);
    // Let Claude fix it once.
    messages = [
      ...messages,
      {
        role: 'user',
        content: [
          {
            type: 'tool_result',
            tool_use_id: call.id,
            is_error: true,
            content: `Nexus could not use this plugin: ${problem}. Fix it and call propose_plugin again with the complete plugin.`,
          },
        ],
      },
    ];
  }
}
